
import React from "react";
import axios from "axios";
import { useForm } from 'react-hook-form';
import RFG from "../../libs/inputs/RFG";
import Swal from "sweetalert2";
import { localCountryCode, seedData } from "../../libs/Libs";
import { Box, Typography, Button, Grid, Card, Stack } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { TASKALL, USERALL, WORKASSIGN, WORKASSIGNALL, COMMITALL } from "../../services/actions";
import { BsFillInfoCircleFill, BsPersonCheckFill } from "react-icons/bs";
import { FormHeroHeader, FormSectionCard, FormFooter, labelStyle } from "../extra/FormLayout";

export default function AssignForm({
    title,
    users = [],
    defaultValues = {},
    onSubmit = (data) => { },
    onChange = (column, data) => { },
    back = () => { } }) {
    let dispatch = useDispatch()
    const location = useLocation()
    const navigate = useNavigate()
    const { data, prevNav } = location.state || {};
    const safeData = data || {};
    defaultValues = { ...defaultValues, ...safeData, employee: safeData.employee_id }
    const [selectedTasks, setSelectedTasks] = React.useState((defaultValues?.tasks || []));
    const [selectAll, setSelectAll] = React.useState(false);

    // ✅ NEW: File states
    const [selectedFiles, setSelectedFiles] = React.useState((defaultValues?.files || []));
    const [selectAllFiles, setSelectAllFiles] = React.useState(false);

    let userallState = useSelector((state) => state?.userallState);
    let taskallState = useSelector((state) => state?.taskallState);
    let workassignallState = useSelector((state) => state?.workassignallState);
    let commitallState = useSelector((state) => state?.commitallState)
    const { handleSubmit, reset, control, setValue, watch, clearErrors, setError, formState: { errors }, } = useForm({ defaultValues: defaultValues, mode: "all" })
    const booleanFields = [
        { name: "verify", label: "Verified", watch: watch("verify") },
        { name: "active", label: "Active", watch: watch("active") },
    ];

    const traineeUsers = React.useMemo(() => {
        const rawList = userallState?.data?.results || [];
        return rawList.filter((u) => {
            if (!u.first_name && !u.username) return false;
            
            // Exclude SuperAdmin / Administrator
            if (u.is_superuser || u.main_role === 'SuperAdmin' || u.first_name?.toLowerCase() === 'administrator') {
                return false;
            }

            const groups = (Array.isArray(u.group) ? u.group : Array.isArray(u.groups) ? u.groups : []).map(
                g => (typeof g === 'string' ? g : g?.name || '')
            );

            // Exclude administrative roles
            if (groups.includes('Work Admin') || groups.includes('Work Manager') || groups.includes('Reviewer') || groups.includes('SuperAdmin') || groups.includes('IT-Manager')) {
                return false;
            }

            // Include Trainees
            if (u.main_role?.toLowerCase() === 'trainee' || groups.includes('Trainee')) {
                return true;
            }

            return false;
        });
    }, [userallState]);
    const fetchEmployess = (page = 0) => {
        const url = page === 0 ? `public/user/${page}/?filter_by={"is_active":true}` : `public/user/?page=${page}&filter_by={"is_active":true}`; // pass page number
        dispatch({ type: `${USERALL}_GET`, payload: url });
    };
    const fetchTasks = (id = 0) => {
        const url = `work/task/0/?filter_by={"work_id":${id}}`; // pass page number
        dispatch({ type: `${TASKALL}_GET`, payload: url });
    };
    const fetchAssignedWwork = (work_id, id = 0) => {
        const url = `work-assign/0/?filter_by={"employee":${id}, "work":${work_id}}`; // pass page number
        dispatch({ type: `${WORKASSIGNALL}_GET`, payload: url });
    };
    const fetchDocs = (id = 0, page = 0) => {
        const url = page === 0 ? `repo/documents/${id}/?repository__client_id=${safeData.repo_uuid || ''}` : `repo/documents/${id}/?page=${page}`;
        dispatch({ type: `${COMMITALL}_GET`, payload: url });
    }
    React.useEffect(() => {
        fetchEmployess(0)
        fetchTasks(safeData?.id)
        fetchDocs()
    }, [])
    React.useEffect(() => {
        const total = taskallState?.data?.results?.length || 0;
        setSelectAll(selectedTasks.length === total && total > 0);
    }, [selectedTasks, taskallState]);

    React.useEffect(() => {
        let results = workassignallState?.data?.results
        if (results?.length > 0) {
            setValue("id", results?.[0]?.id)
            setValue("start_date", results?.[0]?.start_date?.split("T")[0])
            setValue("due_date", results?.[0]?.due_date?.split("T")[0])
            setValue("status", results?.[0]?.status)
            setValue("priority", results?.[0]?.priority)
            setValue("description", results?.[0]?.description)
            setSelectedTasks((results?.[0]?.tasks || []).map(t => t.task))
            setSelectedFiles(results?.[0]?.files || [])
        } else {
            setValue("start_date", "")
            setValue("due_date", "")
            setValue("status", 0)
            setValue("priority", "")
            setValue("description", "")
            setSelectedTasks([])
            setSelectedFiles([])
        }
    }, [workassignallState?.data?.results])

    React.useEffect(() => {
        fetchAssignedWwork(safeData?.id, watch('employee'))
    }, [watch('employee')])

    const handleSelectAll = (e) => {
        const checked = e.target.checked;
        setSelectAll(checked);

        if (checked) {
            const allowed = taskallState?.data?.results?.filter((task) => (!task.assigned_to || task.assigned_to == watch("employee")));
            const allIds = allowed.map((task) => task.id);
            setSelectedTasks(allIds);
        } else {
            setSelectedTasks([]);
        }
    };
    const handleCheckboxChange = (id) => {
        setSelectedTasks((prev) =>
            prev.includes(id)
                ? prev.filter((item) => item !== id)
                : [...prev, id]
        );
    };

    // =========================
    // FILE HANDLERS (NEW)
    // =========================
    const handleFileSelectAll = (e) => {
        const checked = e.target.checked;
        setSelectAllFiles(checked);

        if (checked) {
            const allIds = commitallState?.data?.results?.map(f => f.id);
            setSelectedFiles(allIds);
        } else {
            setSelectedFiles([]);
        }
    };

    const handleFileCheckboxChange = (id) => {
        setSelectedFiles(prev =>
            prev.includes(id)
                ? prev.filter(item => item !== id)
                : [...prev, id]
        );
    };

    const downloadFile = async (docId, commitId, filename) => {
        const response = await axios.get(
            `https://repo.plakal.in/api/download/${docId}?commit_id=${commitId}`,
            {
                responseType: "blob"
            }
        );
        const disposition = response.headers["content-disposition"];
        if (disposition) {
            const match = disposition.match(/filename="(.+)"/);
            if (match) filename = match[1];
        }
        const blob = new Blob([response.data], {
            type: response.headers["content-type"]
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    };

    return (
        <Box sx={{ py: 3, px: { xs: 1, md: 3 }, maxWidth: '1400px', mx: 'auto' }}>
            <FormHeroHeader
                icon={<BsPersonCheckFill size={26} />}
                title={`Assign ${safeData.work_name || 'Work'} to Employee`}
                subtitle="Select responsible staff members, allocate specific tasks, and attach supporting documentation."
                badgeText="Work Assignment"
            />
            <form
                onSubmit={handleSubmit((payload) => {
                    payload.tasks = selectedTasks
                    payload.files = selectedFiles
                    payload.work = safeData.id
                    let action = `${WORKASSIGN}_PATCH`
                    payload.id = 0
                    if (selectedTasks?.length == 0) {
                        Swal.fire({
                            title: "Are you sure?",
                            text: "Unassigned all tasks",
                            icon: "warning",
                            showCancelButton: true,
                            confirmButtonColor: "#74c5ff",
                            cancelButtonColor: "#f88e8e",
                            confirmButtonText: "Yes, delete it!"
                        }).then((result) => {
                            if (result.isConfirmed) {
                                dispatch({ type: action, payload, callback: () => fetchTasks(safeData?.id) })
                            }
                        });
                    } else {
                        dispatch({ type: action, payload, callback: () => fetchTasks(safeData?.id) })
                    }
                })}>
                <FormSectionCard
                    icon={<BsPersonCheckFill size={20} />}
                    title="Assignment Parameters & Resource Allocation"
                    subtitle="Configure employee assignment, timelines, priority, tasks, and files"
                >
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Stack spacing={2.5}>
                                <RFG name="employee" label="Employee *" control={control} labelStyle={labelStyle} rules={{ required: true }}>
                                    <select>
                                        <option value="">Select Employee</option>
                                        {traineeUsers.map((emp) => (
                                            <option key={emp.id} value={emp.id}>
                                                {emp.first_name || emp.username} (Trainee)
                                            </option>
                                        ))}
                                    </select>
                                </RFG>
                                {workassignallState?.data?.results?.length > 0 &&
                                    <RFG name="re_assign_employee" label="Re-Assign to" control={control} labelStyle={labelStyle}>
                                        <select>
                                            <option value="">Select Employee</option>
                                            {traineeUsers.map((user) => (
                                                <option key={user.id} value={user.id}>
                                                    {user.first_name || user.username} (Trainee)
                                                </option>
                                            ))}
                                        </select>
                                    </RFG>}
                                <RFG name="start_date" label="Start Date *" control={control} labelStyle={labelStyle} rules={{
                                    required: true
                                }}>
                                    <input type="date" autoComplete="false" />
                                </RFG>

                                <RFG name="due_date" label="Target Due Date *" control={control} labelStyle={labelStyle} rules={{
                                    required: true
                                }}>
                                    <input type="date" autoComplete="false" />
                                </RFG>
                                <RFG name="priority" label="Priority *" control={control} labelStyle={labelStyle} rules={{ required: true }}>
                                    <select>
                                        <option value="">Select Priority</option>
                                        {[
                                            { id: 1, company_name: "Low" },
                                            { id: 2, company_name: "Medium" },
                                            { id: 3, company_name: "High" },
                                        ].map((company) => <option key={company.id} value={company.id}>{company.company_name}</option>)}
                                    </select>
                                </RFG>

                                <RFG name="description" label="Description *" control={control} labelStyle={labelStyle} rules={{
                                    required: true,
                                    maxLength: {
                                        value: 1000,
                                        message: `Cannot exceed 1000 characters`,
                                    }
                                }}>
                                    <textarea style={{ height: 108 }} autoComplete="false" placeholder="Provide assignment notes..." rows={4} />
                                </RFG>
                            </Stack>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', p: 2, bgcolor: '#f8fafc', height: '100%' }}>
                                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5} pb={1} borderBottom="1px solid #e2e8f0">
                                    <Typography fontSize={14} fontWeight={700} color="#0f172a">Tasks to Assign</Typography>
                                    <Box display="flex" alignItems="center">
                                        <input
                                            type="checkbox"
                                            defaultChecked={selectAll}
                                            onChange={handleSelectAll}
                                            style={{ maxWidth: 18, marginRight: 6 }}
                                        />
                                        <Typography fontSize={12} color="#475569" fontWeight={600}>Select All</Typography>
                                    </Box>
                                </Box>
                                {selectedTasks?.length == 0 && <Typography color="error" fontSize={12} mb={1}>No task selected.</Typography>}

                                <Box sx={{ maxHeight: 350, overflowY: 'auto', pr: 0.5 }}>
                                    {taskallState?.data?.results?.map((task) => (
                                        <Box
                                            key={task.id}
                                            sx={{
                                                background: selectedTasks.includes(task.id) ? "#eff6ff" : "#ffffff",
                                                border: selectedTasks.includes(task.id) ? "1px solid #93c5fd" : "1px solid #cbd5e1",
                                                borderRadius: '8px',
                                                p: 1.2,
                                                my: 0.8,
                                                transition: 'all 0.15s'
                                            }}
                                        >
                                            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                                                <input
                                                    disabled={(task.assigned_to && task.assigned_to != watch("employee"))}
                                                    type="checkbox"
                                                    value={task.id}
                                                    checked={selectedTasks.includes(task.id)}
                                                    onChange={() => handleCheckboxChange(task.id)}
                                                    style={{ maxWidth: 16 }}
                                                />
                                                <Typography width={1} fontSize={13} fontWeight={600} color="#1e293b">{task?.task_name}</Typography>
                                                <Typography fontSize={11} sx={{ px: 1, py: 0.2, bgcolor: '#e2e8f0', borderRadius: '4px', fontWeight: 600 }}>{task?.status}</Typography>
                                            </Box>
                                            {(task.assigned_to && task.assigned_to != watch("employee")) && (
                                                <Box sx={{ mt: 0.8, pt: 0.6, borderTop: '1px dashed #cbd5e1', fontSize: 11, color: '#b45309', display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                                    <BsFillInfoCircleFill fontSize={14} /> Assigned to <strong>{task.assigned_to_name}</strong>
                                                </Box>
                                            )}
                                        </Box>
                                    ))}
                                    {taskallState?.data?.results?.length == 0 && (
                                        <Box sx={{ borderRadius: 2, p: 3, textAlign: 'center', bgcolor: '#ffffff', border: '1px dashed #cbd5e1', color: '#64748b', fontSize: 13, fontWeight: 500 }}>
                                            No tasks found for this work
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', p: 2, bgcolor: '#f8fafc', height: '100%' }}>
                                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5} pb={1} borderBottom="1px solid #e2e8f0">
                                    <Typography fontSize={14} fontWeight={700} color="#0f172a">Repository Files</Typography>
                                    <Box display="flex" alignItems="center">
                                        <input
                                            type="checkbox"
                                            defaultChecked={selectAllFiles}
                                            onChange={handleFileSelectAll}
                                            style={{ maxWidth: 18, marginRight: 6 }}
                                        />
                                        <Typography fontSize={12} color="#475569" fontWeight={600}>Select All</Typography>
                                    </Box>
                                </Box>

                                {(selectedFiles.length === 0 && commitallState?.data?.results?.length != 0) && (
                                    <Typography color="error" fontSize={12} mb={1}>At least one file must be selected.</Typography>
                                )}

                                <Box sx={{ maxHeight: 350, overflowY: 'auto', pr: 0.5 }}>
                                    {commitallState?.data?.results?.map(file => (
                                        <Box
                                            key={file?.id}
                                            sx={{
                                                background: selectedFiles.includes(file.id) ? "#f0fdf4" : "#ffffff",
                                                border: selectedFiles.includes(file.id) ? "1px solid #86efac" : "1px solid #cbd5e1",
                                                borderRadius: '8px',
                                                p: 1.2,
                                                my: 0.8,
                                                display: 'flex', alignItems: 'center',
                                                gap: 1.5, justifyContent: 'space-between'
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedFiles.includes(file.id)}
                                                onChange={() => handleFileCheckboxChange(file.id)}
                                                style={{ maxWidth: 16 }}
                                            />
                                            <Typography title={file.description} width={1} fontSize={13} fontWeight={500} color="#1e293b" noWrap>
                                                {file.original_filename}
                                            </Typography>
                                            <Typography fontSize={12} fontWeight={600} color={'#2563eb'} sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                                onClick={() => { downloadFile(file.id, file.base_commit, file.original_filename) }}
                                            >
                                                Download
                                            </Typography>
                                        </Box>
                                    ))}
                                    {commitallState?.data?.results?.length == 0 && (
                                        <Box sx={{ borderRadius: 2, p: 3, textAlign: 'center', bgcolor: '#ffffff', border: '1px dashed #cbd5e1', color: '#64748b', fontSize: 13, fontWeight: 500 }}>
                                            No repository files found
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </FormSectionCard>

                <FormFooter onCancel={() => navigate('/works/')} backText="Back" submitText="Assign Work" backTestId="back-assign-btn" saveTestId="save-assign-btn" />
            </form>
        </Box>
    );
}


